import { useMemo, useRef, useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import AdBanner, { AdSidebar } from "@/components/feature/AdBanner";
import { useAnalytics } from "@/hooks/useAnalytics";
import { useYoutubeVideos, type YoutubeVideo } from "@/hooks/useYoutubeVideos";
import { usePageSeo } from "@/hooks/usePageSeo";

export default function BotafogoTVPage() {
  const { videos, liveVideo, loading, error } = useYoutubeVideos(20);
  const [selectedVideo, setSelectedVideo] = useState<YoutubeVideo | null>(null);
  const playerRef = useRef<HTMLDivElement | null>(null);

  useAnalytics("/tv");
  usePageSeo({
    title: "Botafogo TV | Videos, entrevistas e melhores momentos",
    description: "Videos do Botafogo, Botafogo TV, entrevistas, melhores momentos, jogos, bastidores, Garrincha, Danilo e noticias do Glorioso.",
    keywords: "Botafogo TV, Botafogo, videos Botafogo, Garrincha, Danilo, FogaoNet, melhores momentos Botafogo",
  });

  const featuredVideo =
    selectedVideo ||
    liveVideo ||
    (videos.length > 0 ? videos[0] : null);

  function handleSelectVideo(video: YoutubeVideo) {
    setSelectedVideo(video);

    setTimeout(() => {
      playerRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);
  }

  return (
    <div className="min-h-screen bg-white relative">
      <Navbar />

      <div className="absolute top-0 left-0 right-0 h-48 md:h-64 bg-gradient-to-b from-alvinegro-black to-white z-0" />

      <main className="pt-20 md:pt-24 pb-12 relative z-10">
        <div className="w-full px-4 md:px-8">
          <Breadcrumb items={[{ label: "Início", path: "/" }, { label: "Botafogo TV" }]} />

          <div className="mb-8">
            <h1 className="font-serif font-bold text-3xl md:text-4xl text-alvinegro-black mb-4">
              Botafogo TV
            </h1>
            <p className="text-alvinegro-muted text-base">
              Vídeos, entrevistas, melhores momentos e tudo sobre o Glorioso em vídeo
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-12">
            <div className="lg:col-span-2">
              <div className="mb-6">
                <AdBanner variant="leaderboard" label="Publicidade" />
              </div>

              {loading ? (
                <div className="space-y-6 animate-pulse">
                  <div className="aspect-video bg-alvinegro-light rounded-2xl" />
                  <div className="h-6 bg-alvinegro-light rounded w-3/4" />
                  <div className="h-4 bg-alvinegro-light rounded w-full" />
                  <div className="h-4 bg-alvinegro-light rounded w-2/3" />
                </div>
              ) : error ? (
                <div className="bg-red-50 border border-red-200 text-red-600 rounded-2xl p-6">
                  {error}
                </div>
              ) : featuredVideo ? (
                <>
                  <section ref={playerRef} className="mb-10">
                    <div className="flex items-center gap-3 mb-4">
                      <h2 className="font-serif font-bold text-xl text-alvinegro-black">
                        {liveVideo && featuredVideo.id === liveVideo.id
                          ? "Ao vivo agora"
                          : "Vídeo em destaque"}
                      </h2>

                      {liveVideo && featuredVideo.id === liveVideo.id && (
                        <span className="bg-red-500 text-white text-xs font-bold px-3 py-1 rounded-full animate-pulse">
                          AO VIVO
                        </span>
                      )}
                    </div>

                    <div className="aspect-video rounded-2xl overflow-hidden bg-black shadow-lg">
                      <iframe
                        key={featuredVideo.id}
                        className="w-full h-full"
                        src={`https://www.youtube.com/embed/${featuredVideo.id}?autoplay=1&mute=1&rel=0&playsinline=1`}
                        title={featuredVideo.title}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>

                    <div className="mt-4">
                      <h3 className="font-serif font-bold text-2xl text-alvinegro-black leading-tight">
                        {featuredVideo.title}
                      </h3>

                      <p className="text-sm text-alvinegro-muted mt-2">
                        {new Date(featuredVideo.publishedAt).toLocaleDateString("pt-BR")}
                      </p>

                      {featuredVideo.description && (
                        <p className="text-alvinegro-muted text-sm mt-3 leading-relaxed line-clamp-3">
                          {featuredVideo.description}
                        </p>
                      )}
                    </div>
                  </section>

                  <div className="mt-8 mb-8">
                    <AdBanner variant="leaderboard" label="Publicidade" />
                  </div>

                  <h2 className="font-serif font-bold text-xl text-alvinegro-black mb-6">
                    Últimos vídeos
                  </h2>

                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
                    {videos.map((video) => (
                      <button
                        key={video.id}
                        type="button"
                        onClick={() => handleSelectVideo(video)}
                        className="group text-left cursor-pointer"
                      >
                        <div className="relative rounded-2xl overflow-hidden aspect-video mb-3 bg-alvinegro-light">
                          <img
                            src={video.thumbnail}
                            alt={video.title}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          />

                          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />

                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center group-hover:bg-white transition-colors">
                              <i className="ri-play-fill text-xl text-red-600" />
                            </div>
                          </div>

                          {featuredVideo.id === video.id && (
                            <span className="absolute bottom-3 left-3 bg-white text-alvinegro-black text-xs font-bold px-2 py-1 rounded">
                              Selecionado
                            </span>
                          )}
                        </div>

                        <h3 className="font-semibold text-sm text-alvinegro-black leading-snug line-clamp-2 group-hover:text-alvinegro-gray transition-colors">
                          {video.title}
                        </h3>

                        <p className="text-xs text-alvinegro-muted mt-1">
                          {new Date(video.publishedAt).toLocaleDateString("pt-BR")}
                        </p>
                      </button>
                    ))}
                  </div>
                </>
              ) : (
                <div className="bg-alvinegro-light rounded-2xl p-6 text-alvinegro-muted">
                  Nenhum vídeo encontrado no canal.
                </div>
              )}
            </div>

            <aside>
              <AdSidebar label="Publicidade" className="mb-6" />

              <div className="bg-alvinegro-light rounded-2xl p-6">
                <h3 className="font-serif font-bold text-lg text-alvinegro-black mb-4">
                  Canal Oficial
                </h3>

                <a
                  href="https://www.youtube.com/@BotafogoTV/videos"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between p-3 bg-white rounded-xl hover:bg-alvinegro-border transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-red-50 flex items-center justify-center">
                      <i className="ri-youtube-line text-red-600" />
                    </div>

                    <div>
                      <p className="text-sm font-medium text-alvinegro-black">
                        Botafogo TV
                      </p>
                      <p className="text-xs text-alvinegro-muted">
                        Canal oficial no YouTube
                      </p>
                    </div>
                  </div>

                  <i className="ri-arrow-right-line text-alvinegro-muted" />
                </a>
              </div>
            </aside>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
